import React, { useState, useEffect, useMemo } from 'react';
const defaultRectObj = { bottom: 0, height: 0, left: 0, right: 0, top: 0, width: 0, x: 0, y: 0, toJSON: () => {} };
/**
 * Custom hook that returns the CSS properties for positioning a dropdown list relative to a button element.
 * @param {React.RefObject<HTMLButtonElement>} buttonElement - The ref object for the button element.
 * @param {React.RefObject<HTMLDivElement>} listElement - The ref object for the dropdown list element.
 * @returns {React.CSSProperties} - The CSS properties for positioning the dropdown list.
 */
export const useDropdownPosition = (buttonElement: React.RefObject<HTMLButtonElement>, listElement: React.RefObject<HTMLDivElement>): React.CSSProperties => {
    const [{ width, height }, setViewportSize] = useState({ height: window.innerHeight, width: window.innerWidth });
    const [buttonElDOMRect, setButtonElDOMRect] = useState<DOMRect>(defaultRectObj);
    const [listElDOMRect, setListElDOMRect] = useState<DOMRect>(defaultRectObj);

    useEffect(() => {
        const el = (buttonElement?.current?.lastElementChild || buttonElement.current) as HTMLElement;
        setButtonElDOMRect(el.getBoundingClientRect());
        // below code is to update button's DOMRect when layout shift happened
        const observer = new MutationObserver(() => {
            setButtonElDOMRect(el.getBoundingClientRect());
        });
        observer.observe(buttonElement.current as HTMLElement, { attributes: true, subtree: true, childList: true });
        return () => {
            observer.disconnect();
        };
    }, [buttonElement, width, height]);

    useEffect(() => {
        const el = listElement.current as HTMLElement;
        setListElDOMRect(el.getBoundingClientRect());
    }, [listElement, width, height]);

    useEffect(() => {
        const handleResize = () => setViewportSize({ height: window.innerHeight, width: window.innerWidth });
        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    const dropdownPositionCSS: React.CSSProperties = useMemo(() => {
        const margin = 16;
        const hasSpaceBelow = buttonElDOMRect.y + buttonElDOMRect.height / 2 + listElDOMRect.height + margin <= height;
        const hasSpaceAbove = buttonElDOMRect.y - buttonElDOMRect.height / 2 - listElDOMRect.height - margin >= 0;
        const hasSpaceLeft = buttonElDOMRect.x + buttonElDOMRect.width / 2 - listElDOMRect.width - margin >= 0;
        const hasSpaceRight = buttonElDOMRect.x + buttonElDOMRect.width / 2 + listElDOMRect.width + margin <= width;

        const dropdownPosition: ['right' | 'left' | 'middle', 'top' | 'bottom'] = ['right', 'bottom'];

        if (!hasSpaceBelow && hasSpaceAbove) dropdownPosition[1] = 'top';
        if (hasSpaceRight) dropdownPosition[0] = 'right';
        else if (hasSpaceLeft) dropdownPosition[0] = 'left';
        else dropdownPosition[0] = 'middle';

        const dropdownPositionCSS: React.CSSProperties = { position: 'fixed' };
        const [horizontalAlign, verticalAlign] = dropdownPosition;
        switch (horizontalAlign) {
            case 'left':
                dropdownPositionCSS.right = Math.ceil(width - buttonElDOMRect.x - buttonElDOMRect.width / 2);
                break;
            case 'right':
                dropdownPositionCSS.left = Math.ceil(buttonElDOMRect.x + buttonElDOMRect.width / 2);
                break;
            case 'middle':
                dropdownPositionCSS.left = Math.ceil((width - listElDOMRect.width) / 2);
                break;
            default:
                break;
        }
        switch (verticalAlign) {
            case 'bottom':
                dropdownPositionCSS.top = Math.ceil(buttonElDOMRect.y + buttonElDOMRect.height / 2);
                break;
            case 'top':
                dropdownPositionCSS.bottom = Math.ceil(height - buttonElDOMRect.y - buttonElDOMRect.height / 2);
                break;
            default:
                break;
        }

        return dropdownPositionCSS;
    }, [width, height, buttonElDOMRect, listElDOMRect]);

    return dropdownPositionCSS;
};
